import type { FormEvent } from 'react';
import { motion } from "motion/react";
import { SanitizedMarkup } from 'src/lib/common/ui/SanitizedMarkup';
import { Offscreen } from 'src/lib/common/ui/Offscreen';
import type { WizardPageViewModel } from '../wizardPageViewModel';
import { useTranslation } from '../../../i18n/useTranslation';
import { InputCheckbox } from './InputCheckbox';
import { ArrowLeft, ArrowRight } from 'src/lib/assets/icons';
import styles from './InputSection.module.scss';
import { LoadingIndicator } from 'src/lib/common/ui/LoadingIndicator';

interface InputSectionProps {
  viewModel: WizardPageViewModel,
  title: string,
}

export function InputSection({ viewModel, title }: InputSectionProps) {
  const { t } = useTranslation();

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    viewModel.gotToNextStep();
  };

  if (viewModel.isLoadingInitialOptions) {
    return <LoadingIndicator displayText={ false } />;
  }

  let buttonLabel = t('wizard_next_step');
  switch (viewModel.currentStep?.stepName) {
    case 'flavourSelect':
      buttonLabel = t('wizard_next_step_taste');
      break;
    case 'tasteSelect':
      buttonLabel = t('wizard_next_step_mouthfeel');
      break;
    case 'mouthfeelSelect':
      buttonLabel = t('wizard_submit');
      break;
  }

  const prevButtonClick = () => {
    viewModel.goToPreviousStep();
  };

  return (
    <div className={ styles.container }>
      <form onSubmit={ onSubmit } className={ styles.form }>

        <motion.div className={ styles.formContent } initial={{  opacity: 0 }} animate={{  opacity: 1 }} transition={{ duration: 0.4 }}>
          <fieldset className={ styles.fieldset }>
            <legend className={ styles.legend }>
              <h2 className={ styles.title }>
                <SanitizedMarkup html={ title } />
              </h2>
            </legend>

            <div className={ styles.optionListHolder } data-loading={ viewModel.isLoadingNextOptions}>
              <ul className={ styles.optionList }>
                {
                  viewModel.options.map((option) => (
                    <li key={ option.id }>
                      <InputCheckbox
                        htmlId={ option.id }
                        label={ option.title }
                        checked={ option.checked }
                        disabled={ option.disabled || viewModel.isLoadingNextOptions }
                        onChange={ (checked) => viewModel.setOption(option, checked) }
                      />
                    </li>
                  ))
                }
              </ul>
            </div>

          </fieldset>
        </motion.div>

        <div className={ styles.formActions }>
          <button type="button" className={ styles.ctaPrevious } onClick={ prevButtonClick }>
            <Offscreen>
              { t('wizard_previous_step') }
            </Offscreen>
            <ArrowLeft />
          </button>
          <button type="submit" className={ styles.ctaSubmit } disabled={ viewModel.isLoadingNextOptions }>
            <span>{ buttonLabel }</span>
            <ArrowRight />
          </button>
        </div>

      </form>
    </div>
  );
}
